import { CommonModule } from '@angular/common';
import { Component, NgModule, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AccountGroup, AccountGroupService } from '../../account-group';
import { SidepanelService } from '../../navigation/sidepanel.service';
import { CreateAndAssociateRequest, CreateAndAssociateResponse, UserService } from '@vendasta/business-center';

@Component({
  selector: 'bc-invite-user-sidebar',
  templateUrl: './invite-user-sidebar.component.html',
  styleUrls: ['./invite-user-sidebar.component.scss'],
  standalone: false,
})
export class InviteUserSidebarComponent implements OnInit, OnDestroy {
  private account: AccountGroup;
  formGroup: UntypedFormGroup;
  formSubmitting: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  _subscriptions: Subscription[] = [];

  constructor(
    private userService: UserService,
    public sidepanelService: SidepanelService,
    private accountGroupService: AccountGroupService,
    private snackbarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.formGroup = new UntypedFormGroup({
      firstName: new UntypedFormControl(),
      lastName: new UntypedFormControl(),
      email: new UntypedFormControl(),
      phone: new UntypedFormControl(),
    });
    this._subscriptions.push(
      this.accountGroupService.currentAccountGroup$.subscribe((account) => {
        this.account = account;
      }),
    );
  }

  closeSidebar(): void {
    this.formSubmitting.next(false);
    this.formGroup.reset();
    this.sidepanelService.close();
  }

  submit(): void {
    if (this.formGroup.invalid) {
      return;
    }

    const formValues = this.formGroup.getRawValue();
    this.formSubmitting.next(true);
    this.createAndAssociateUser().subscribe({
      next: () => {
        this.closeSidebar();
        this.snackbarService.openSuccessSnack('SETTINGS.MY_TEAM.INVITE_MEMBER_SIDEBAR.SUCCESS', {
          interpolateTranslateParams: {
            email: formValues.email,
          },
        });
      },
      error: (error) => {
        this.closeSidebar();
        if (error.status === 409) {
          this.snackbarService.openErrorSnack('SETTINGS.MY_TEAM.INVITE_MEMBER_SIDEBAR.MEMBER_EXISTS_ERROR', {
            interpolateTranslateParams: {
              email: formValues.email,
              companyName: this.account.companyName,
            },
          });
        } else {
          this.snackbarService.openErrorSnack('SETTINGS.MY_TEAM.INVITE_MEMBER_SIDEBAR.ERROR');
        }
      },
    });
  }

  createAndAssociateUser(): Observable<CreateAndAssociateResponse> {
    const formValues = this.formGroup.getRawValue();

    return this.userService.createAndAssociate({
      email: formValues.email,
      partnerId: this.account.partnerId,
      accountGroupId: this.account.accountGroupId,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      phone: `+${formValues.phone}`,
      sendWelcomeEmail: true,
      failOnExistingUser: false,
      notificationsFlag: true,
      failOnExistingAssociation: true,
    } as CreateAndAssociateRequest);
  }

  get formSubmitting$(): Observable<boolean> {
    return this.formSubmitting.asObservable();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((sub) => sub.unsubscribe());
  }
}

@NgModule({
  imports: [CommonModule, MatIconModule, MatButtonModule, GalaxyInputModule, ReactiveFormsModule, TranslateModule],
  declarations: [InviteUserSidebarComponent],
})
export class InviteUserSidebarModule {}
